import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildCheckupRows, effectiveWebIdentifiers } from '@heybesafe/shared';
import type { VaultItem } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { HbsButton } from '../components/hbs/Button';
import { HbsSwitch } from '../components/hbs/Switch';
import { Icon } from '../components/hbs/Icon';
import { BreachCheckDisclosureModal } from '../components/BreachCheckDisclosureModal';
import { findExposed } from '../lib/checkupExposed';
import { ackDisclosure, hasAckedDisclosure, isBreachCheckEnabled, setBreachCheckEnabled } from '../lib/checkupSettings';
import { changePasswordTarget } from '../lib/changePasswordTarget';

/**
 * Security Checkup — the pull surface for the password checkup (weak · reused ·
 * exposed). Weak/reused run locally; the exposed (data-breach) check uses HIBP
 * k-anonymity via the proxy, cached ≤ weekly. Rows are **collapsed by domain**
 * and ordered **financial first → most-reused → severity** (shared
 * `buildCheckupRows`). No-nag, positive-framed, never renders a plaintext
 * password.
 */
function isCheckupLogin(item: VaultItem): boolean {
  return !!item.password && (item.type === 'login' || item.type === undefined);
}

type BreachState = 'idle' | 'checking' | 'done' | 'error' | 'disabled';

/** The breach check is "on" only when enabled AND the one-time disclosure has
 *  been acknowledged — the two always move together, so an enabled-but-unacked
 *  state (legacy / manually-cleared) reads as off and re-prompts on enable. */
function breachOn(): boolean {
  return isBreachCheckEnabled() && hasAckedDisclosure();
}

export default function CheckupPage(): JSX.Element {
  const { items, itemsLoaded, loadingStep } = useSafe();
  const navigate = useNavigate();

  const logins = useMemo(() => items.filter(isCheckupLogin), [items]);
  const loginById = useMemo(() => new Map(logins.map((i) => [i.id, i])), [logins]);

  /**
   * "Fix" sends the user to the site to change the password: its dedicated
   * change-password page when we know one, else the site root — opened in a new
   * tab (the real website, not the in-app editor). A URL-less login has no site
   * to visit, so we fall back to the item editor there.
   */
  function fixRow(repId: string): void {
    const item = loginById.get(repId);
    const target = item ? changePasswordTarget(item) : null;
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
    else navigate(`/safe/${repId}/edit`);
  }

  const [exposedById, setExposedById] = useState<Map<string, number>>(new Map());
  const [breachState, setBreachState] = useState<BreachState>('idle');
  // Whether breach checking is on, mirrored into state so the top-of-page toggle
  // re-renders. The one-time disclosure modal is opened from the toggle (turning
  // it on the first time) or the "Learn what's sent" link.
  const [breachEnabled, setBreachEnabled] = useState(() => breachOn());
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const runIdRef = useRef(0);

  function runCheck(ls: typeof logins): void {
    const myId = ++runIdRef.current;
    setBreachState('checking');
    findExposed(ls)
      .then((res) => {
        if (runIdRef.current !== myId) return;
        setExposedById(res.exposedById);
        setBreachState(res.failed ? 'error' : 'done');
      })
      .catch(() => {
        if (runIdRef.current === myId) setBreachState('error');
      });
  }

  /** Turn the breach check on (via the disclosure the first time) or off. */
  function handleBreachToggle(next: boolean): void {
    if (next) {
      if (hasAckedDisclosure()) {
        setBreachCheckEnabled(true);
        setBreachEnabled(true);
        if (logins.length > 0) runCheck(logins);
      } else {
        // Consent first; the modal's onEnable enables + runs.
        setDisclosureOpen(true);
      }
    } else {
      setBreachCheckEnabled(false);
      setBreachEnabled(false);
      runIdRef.current++;
      setExposedById(new Map());
      setBreachState('disabled');
    }
  }

  useEffect(() => {
    if (logins.length === 0) {
      runIdRef.current++;
      setExposedById(new Map());
      setBreachState('idle');
      return;
    }
    if (!breachOn()) {
      setBreachState('disabled');
      return;
    }
    runCheck(logins);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logins]);

  const { rows, strongCount, total, reviewCount } = useMemo(() => {
    const exposedIds = new Set(exposedById.keys());
    const input = logins.map((i) => ({
      id: i.id,
      name: i.name,
      password: i.password,
      webIdentifiers: effectiveWebIdentifiers(i),
    }));
    return buildCheckupRows(input, exposedIds);
  }, [logins, exposedById]);

  const loading = !itemsLoaded && items.length === 0;
  const summary =
    `${strongCount} of ${total} password${total === 1 ? '' : 's'} ` +
    `${strongCount === 1 ? 'is' : 'are'} strong`;
  const breachNote =
    breachState === 'checking'
      ? 'Checking for exposed passwords…'
      : breachState === 'error'
        ? "Couldn't check for breaches right now — try again later."
        : null;

  return (
    <div className="p-9 max-w-2xl mx-auto flex flex-col gap-8">
      <div>
        <h1 className="font-display font-semibold text-[32px] tracking-hbs-tight text-hbs-text">
          Security Checkup
        </h1>
        <p className="text-[13px] text-hbs-text-mute mt-1">
          A quick look at the strength of your saved passwords. Checked privately
          on this device — your passwords never leave it.
        </p>
      </div>

      {/* Breach-check toggle — top of the page, mirroring the mobile apps (it
          used to live in Settings). Turning it on the first time runs the
          one-time disclosure. */}
      {!loading && (
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[14px] font-medium text-hbs-text">Check for data breaches</p>
            <p className="text-[12px] text-hbs-text-mute mt-0.5">
              Flags passwords found in known data breaches. Only a scrambled
              partial fingerprint is ever sent — never a password.{' '}
              <button
                type="button"
                id="breach-learn-more-button"
                onClick={() => setDisclosureOpen(true)}
                className="font-semibold text-hbs-accent hover:underline"
              >
                Learn what's sent
              </button>
            </p>
          </div>
          <HbsSwitch
            id="breach-check-toggle"
            label="Check for data breaches"
            checked={breachEnabled}
            onChange={handleBreachToggle}
          />
        </section>
      )}

      {loading ? (
        <div role="status" aria-live="polite" className="text-hbs-text-mute text-[13px]">
          {loadingStep ?? 'Loading your safe…'}
        </div>
      ) : total === 0 ? (
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 flex items-center gap-3">
          <Icon name="safe" size={22} className="text-hbs-text-mute" />
          <p className="text-[14px] text-hbs-text">
            No logins to check yet. Add some logins and your checkup will appear here.
          </p>
        </section>
      ) : (
        <>
          <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-hbs-surface-alt flex items-center justify-center flex-shrink-0">
              <Icon
                name={reviewCount === 0 ? 'check' : 'checklist'}
                size={22}
                className={reviewCount === 0 ? 'text-hbs-primary' : 'text-hbs-text'}
              />
            </div>
            <div className="min-w-0">
              <p className="font-display font-semibold text-[18px] text-hbs-text">{summary}</p>
              <p className="text-[13px] text-hbs-text-mute mt-0.5">
                {reviewCount === 0
                  ? 'Everything looks good — nothing to fix right now.'
                  : `${reviewCount} ${reviewCount === 1 ? 'site' : 'sites'} to review.`}
              </p>
              {breachNote && (
                <p role="status" className="text-[12px] text-hbs-text-mute mt-1.5">
                  {breachNote}
                </p>
              )}
            </div>
          </section>

          {rows.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="font-display text-[15px] text-hbs-text px-1">Sites to review</h2>
              <ul className="flex flex-col gap-2">
                {rows.map((row) => (
                  <li key={row.repId}>
                    <div className="bg-hbs-surface border border-hbs-border rounded-hbs-lg px-4 py-3 flex items-center gap-3">
                      <Icon name="login" size={18} className="text-hbs-text-mute flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-[14px] font-medium text-hbs-text truncate">{row.name}</p>
                          {row.financial && (
                            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-hbs-primary">
                              Financial
                            </span>
                          )}
                          {row.loginCount > 1 && (
                            <span className="shrink-0 text-[10px] font-semibold text-hbs-text-mute border border-hbs-border rounded px-1.5 py-0.5">
                              {row.loginCount} logins
                            </span>
                          )}
                        </div>
                        <p className={`text-[12px] mt-0.5 ${row.tier <= 1 ? 'text-hbs-text' : 'text-hbs-text-mute'}`}>
                          {row.reason}
                        </p>
                      </div>
                      <HbsButton
                        id={`checkup-fix-${row.repId}`}
                        variant="ghost"
                        title={`Change the password for ${row.name}`}
                        onClick={() => fixRow(row.repId)}
                      >
                        Fix
                      </HbsButton>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <BreachCheckDisclosureModal
        open={disclosureOpen}
        onEnable={() => {
          ackDisclosure();
          setBreachCheckEnabled(true);
          setBreachEnabled(true);
          setDisclosureOpen(false);
          if (logins.length > 0) runCheck(logins);
        }}
        onClose={() => setDisclosureOpen(false)}
      />
    </div>
  );
}
